import { useMemo, useState } from 'react';
import { api, scr } from '../../lib/api';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Appropriation, Position, Warrant } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatCard } from '../../ui/components';
import { useEffect } from 'react';

export function FinanceOverview() {
  const { fy, votes } = useRefData();
  const apprQ = useApiQuery<{ items: Appropriation[] }>(
    fy ? `/api/v1/warrants/appropriations?fiscalYearId=${fy.id}&limit=100` : null,
  );
  const warrantsQ = useApiQuery<{ items: Warrant[] }>(fy ? `/api/v1/warrants/?fiscalYearId=${fy.id}&limit=100` : null);

  const byVote = useMemo(() => {
    const m = new Map<string, { appropriated: number; lines: number }>();
    for (const a of apprQ.data?.items ?? []) {
      const cur = m.get(a.voteId) ?? { appropriated: 0, lines: 0 };
      cur.appropriated += Number.parseFloat(a.revisedAmount);
      cur.lines += 1;
      m.set(a.voteId, cur);
    }
    return m;
  }, [apprQ.data]);

  const totalBudget = [...byVote.values()].reduce((s, v) => s + v.appropriated, 0);
  const issued = (warrantsQ.data?.items ?? []).filter((w) => w.status === 'ISSUED').length;
  const funded = votes.filter((v) => byVote.has(v.id));
  const [selectedVote, setSelectedVote] = useState<string>('');

  if (!fy) return <ErrorNote message="No OPEN fiscal year — create and open one under Chart of accounts." />;

  return (
    <div>
      <PageHeader
        title="National budget overview"
        subtitle={`Fiscal Year ${fy.year} — appropriations by ministry vote`}
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total appropriated" value={scr(totalBudget)} hint="All votes, revised amounts" />
        <StatCard label="Votes funded" value={`${funded.length} / ${votes.length}`} hint="Ministries & offices with budget" />
        <StatCard label="Budget lines" value={apprQ.data?.items.length ?? '—'} hint="Appropriated CoA combinations" />
        <StatCard label="Warrants issued" value={`${issued} / ${warrantsQ.data?.items.length ?? 0}`} hint="Quarterly spending authority" />
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Allocations by ministry</h2>
        <div className="card overflow-x-auto">
          {apprQ.loading ? (
            <Spinner />
          ) : funded.length === 0 ? (
            <EmptyState title="No appropriations yet" hint="Enter the enacted budget under Appropriations." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Vote</th>
                  <th className="table-head">Ministry / office</th>
                  <th className="table-head text-right">Appropriated (revised)</th>
                  <th className="table-head text-right">Budget lines</th>
                  <th className="table-head w-56">Share of budget</th>
                </tr>
              </thead>
              <tbody>
                {funded
                  .map((v) => ({ vote: v, agg: byVote.get(v.id)! }))
                  .sort((a, b) => b.agg.appropriated - a.agg.appropriated)
                  .map(({ vote, agg }) => (
                    <tr
                      key={vote.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelectedVote(selectedVote === vote.id ? '' : vote.id)}
                    >
                      <td className="table-cell font-semibold">{vote.code}</td>
                      <td className="table-cell">{vote.name}</td>
                      <td className="table-cell text-right tabular-nums">{scr(agg.appropriated)}</td>
                      <td className="table-cell text-right">{agg.lines}</td>
                      <td className="table-cell">
                        <div className="h-2 w-full rounded-full bg-slate-100">
                          <div
                            className="h-2 rounded-full bg-brand-500"
                            style={{ width: `${totalBudget > 0 ? Math.max(2, (agg.appropriated / totalBudget) * 100) : 0}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedVote ? <VotePositions voteId={selectedVote} fyId={fy.id} /> : (
        <p className="text-xs text-slate-400">Click a ministry to see its live budget position per line.</p>
      )}
    </div>
  );
}

function VotePositions({ voteId, fyId }: { voteId: string; fyId: string }) {
  const { votes } = useRefData();
  const vote = votes.find((v) => v.id === voteId);
  const [rows, setRows] = useState<Array<{ combo: Combination; position: Position | null }> | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    (async () => {
      const combos = await api.get<{ items: Combination[] }>(`/api/v1/coa/combinations?voteId=${voteId}&limit=100`);
      const positions = await Promise.all(
        combos.items.map((c) => api.get<Position>(`/api/v1/warrants/position/${c.id}?fiscalYearId=${fyId}`).catch(() => null)),
      );
      if (alive) setRows(combos.items.map((combo, i) => ({ combo, position: positions[i] ?? null })));
    })().catch(() => {
      if (alive) setRows([]);
    });
    return () => {
      alive = false;
    };
  }, [voteId, fyId]);

  const funded = rows?.filter((r) => r.position && r.position.appropriated > 0) ?? [];

  return (
    <div>
      <h2 className="mb-2 text-sm font-bold text-slate-700">
        Live position — {vote?.code} {vote?.name}
      </h2>
      <div className="card overflow-x-auto">
        {rows === null ? (
          <Spinner />
        ) : funded.length === 0 ? (
          <EmptyState title="No funded lines for this vote" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Budget line</th>
                <th className="table-head text-right">Appropriated</th>
                <th className="table-head text-right">Allotted</th>
                <th className="table-head text-right">Committed</th>
                <th className="table-head text-right">Available to allot</th>
                <th className="table-head text-right">Available to commit</th>
              </tr>
            </thead>
            <tbody>
              {funded.map(({ combo, position: p }) => (
                <tr key={combo.id} className="hover:bg-slate-50">
                  <td className="table-cell text-xs">{comboLabel(combo)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p!.appropriated)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p!.allotted)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p!.committed)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p!.availableToAllot)}</td>
                  <td className="table-cell text-right font-semibold tabular-nums text-seagreen-600">{scr(p!.availableToCommit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
